<template>
  <template v-if="isAuthenticated">
    <VcButton
      class="add-to-cart__icon-button"
      variant="solid"
      :loading="loading"
      :disabled="disabled || !!errorMessage"
      :title="buttonText"
      size="lg"
      :icon="icon"
      full-width
      @click="onChange"
    >
      {{ buttonText }}
    </VcButton>

    <VcButton
      class="add-to-cart__text-button"
      variant="solid"
      :loading="loading"
      :disabled="disabled || !!errorMessage"
      :title="buttonText"
      size="lg"
      truncate
      full-width
      @click="onChange"
    >
      {{ buttonText }}
    </VcButton>
  </template>

  <div
    v-else
    class="flex flex-col items-center gap-3 rounded-xl border border-neutral-200 bg-white px-5 py-4 text-center shadow-sm"
  >
    <VcIcon name="lock-closed" class="text-neutral-400 [--vc-icon-size:1.75rem]" />

    <p class="text-sm leading-snug text-secondary">
      {{ $t("shared.cart.add_to_cart.sign_in_for_access") }}
    </p>

    <VcButton
      @click="goToSignIn(route.fullPath)"
      color="primary"
      variant="solid"
      size="sm"
      class="w-full uppercase"
    >
      {{ $t("shared.layout.header.link_sign_in") }}
    </VcButton>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/yup";
import { clone } from "lodash";
import { useField } from "vee-validate";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useUser } from "@/shared/account";
import { useErrorsTranslator, useGoogleAnalytics } from "@/core/composables";
import { useSignIn } from "@/shared/sign-in/composables/useSignIn";
import { ValidationErrorObjectType } from "@/core/enums";
import { Logger } from "@/core/utilities";
import { useShortCart } from "@/shared/cart/composables";
import { useNotifications } from "@/shared/notification";
import { useQuantityValidationSchema } from "@/ui-kit/composables";
import type { Product, ShortCartFragment, ShortLineItemFragment, VariationType } from "@/core/api/graphql/types";
import type { NamedValue } from "vue-i18n";

const emit = defineEmits<IEmits>();

const props = defineProps<IProps>();
const { goToSignIn } = useSignIn();
const notifications = useNotifications();

const router = useRouter();
const route = useRoute();
const { isAuthenticated } = useUser();

interface IEmits {
  (event: "update:lineItem", lineItem: ShortLineItemFragment): void;
}

interface IProps {
  product: Product | VariationType;
  reservedSpace?: boolean;
}

const isInStock = computed(
  () => props.product.availabilityData?.isInStock && props.product.availabilityData?.isBuyable,
);
const availableQuantity = computed(() => props.product.availabilityData?.availableQuantity);
const minQuantity = computed(() => props.product.minQuantity);
const maxQuantity = computed(() => props.product.maxQuantity);

const { cart, addToCart, changeItemQuantity } = useShortCart();
const { t } = useI18n();
const ga = useGoogleAnalytics();
const { getTranslation } = useErrorsTranslator("validation_error");
const { quantitySchema } = useQuantityValidationSchema({
  isInStock,
  availableQuantity,
  minQuantity,
  maxQuantity,
});

const loading = ref(false);

const countInCart = computed<number>(() => getLineItem(cart.value?.items)?.quantity || 0);
const minQty = computed<number>(() => minQuantity.value || 1);

const disabled = computed<boolean>(() => loading.value || !props.product.availabilityData?.isAvailable);

const icon = computed<"refresh" | "cart">(() => (countInCart.value ? "refresh" : "cart"));

const buttonText = computed<string>(() =>
  countInCart.value ? t("common.buttons.update_cart") : t("common.buttons.add_to_cart"),
);

const rules = computed(() => toTypedSchema(quantitySchema.value));

const enteredQuantity = ref(!disabled.value ? countInCart.value || minQty.value : undefined);

const { errorMessage, validate } = useField("quantity", rules, {
  initialValue: enteredQuantity,
  validateOnMount: true,
});

/**
 * Process button click to add/update cart line item.
 */
async function onChange() {
  const { valid } = await validate();

  if (!valid || disabled.value) {
    return;
  }

  loading.value = true;

  let lineItem = getLineItem(cart.value?.items);

  let updatedCart: ShortCartFragment | undefined;

  const isAlreadyExistsInTheCart = !!lineItem;
  if (isAlreadyExistsInTheCart) {
    updatedCart = await changeItemQuantity(lineItem!.id, enteredQuantity.value || 0);
    // Redirect to the cart page
    await router.push({ name: "Cart" });
  } else {
    const inputQuantity = enteredQuantity.value || minQty.value;

    updatedCart = await addToCart(props.product.id!, inputQuantity);

    /**
     * Send Google Analytics event for an item added to cart.
     */
    ga.addItemToCart(props.product, inputQuantity);
  }

  lineItem = clone(getLineItem(updatedCart?.items));

  if (!lineItem) {
    Logger.error(onChange.name, 'The variable "lineItem" must be defined');
    notifications.error({
      text: t(
        isAlreadyExistsInTheCart
          ? "common.messages.fail_to_change_quantity_in_cart"
          : "common.messages.fail_add_product_to_cart",
        {
          reason: updatedCart?.validationErrors
            ?.filter(
              (validationError) =>
                validationError.objectId === props.product.id &&
                validationError.objectType === ValidationErrorObjectType.CatalogProduct,
            )
            .map((el) => {
              return getTranslation({
                code: el.errorCode,
                parameters: el.errorParameters?.reduce((acc, err) => {
                  acc[err.key] = err.value;
                  return acc;
                }, {} as NamedValue),
                description: el.errorMessage,
              });
            })
            .join(" "),
        },
      ),
      duration: 4000,
      single: true,
    });
  } else {
    emit("update:lineItem", lineItem);
  }

  loading.value = false;
}

function getLineItem(items?: ShortLineItemFragment[]): ShortLineItemFragment | undefined {
  return items?.find((item) => item.productId === props.product.id);
}
</script>

<style lang="scss">
.add-to-cart {
  @apply @container w-full flex-none;

  &__icon-button.vc-button {
    @apply hidden;

    @container (width > theme("containers.xxs")) {
      @apply hidden;
    }
  }

  &__text-button.vc-button {
    @container (width > theme("containers.xxs")) {
      @apply block w-32;
    }
  }
}

.vc-button__slot {
  font-weight: 700;
}
</style>
